import { useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { appointmentService } from '@lib/supabase/appointment.service'
import { usePatientAuth } from '@hooks/usePatientAuth'
import AppointmentChat from '@components/web/AppointmentChat'
import Button from '@components/web/Button'
import { chatService } from '@lib/supabase/chat.service'
import { queryKeys } from '@lib/queryKeys'

export default function PatientChat() {
  const { appointmentId } = useParams<{ appointmentId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { patient } = usePatientAuth()
  const backPath = (location.state as { backPath?: string } | null)?.backPath ?? '/patient/appointments'

  const chatMetaQuery = useQuery({
    queryKey: queryKeys.chat.patientMeta(appointmentId),
    enabled: !!appointmentId,
    queryFn: async () => {
      const apt = await appointmentService.getAppointmentById(appointmentId!)
      if (!apt) {
        return { error: 'Appointment not found.', patientId: null, providerId: null, providerName: undefined }
      }
      if (apt.service_type !== 'Chat Consultation') {
        return {
          error: 'Chat is only available for Chat Consultation appointments.',
          patientId: null,
          providerId: null,
          providerName: undefined,
        }
      }

      return {
        error: null,
        patientId: apt.patient_id,
        providerId: apt.provider_id,
        providerName: undefined,
      }
    },
  })

  const patientId = chatMetaQuery.data?.patientId ?? null
  const providerId = chatMetaQuery.data?.providerId ?? null
  const providerName = chatMetaQuery.data?.providerName
  const error = !appointmentId ? 'Appointment not found.' : chatMetaQuery.data?.error ?? null
  const isLoading = Boolean(appointmentId) && chatMetaQuery.isPending

  useEffect(() => {
    if (!appointmentId || !patient?.id) return

    void chatService.markAppointmentChatAsSeen(patient.id, appointmentId)

    return () => {
      void chatService.markAppointmentChatAsSeen(patient.id, appointmentId)
    }
  }, [appointmentId, patient?.id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground mt-3">Loading chat…</p>
        </div>
      </div>
    )
  }

  if (error || !patientId || !providerId || !patient?.id) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center max-w-xs">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-3">
            <AlertTriangle size={22} className="text-destructive" />
          </div>
          <p className="text-sm text-foreground font-medium">{error ?? 'Unable to load chat'}</p>
          <Button className="mt-4" variant="outline" size="sm" onClick={() => navigate('/patient/appointments')}>
            Back to Appointments
          </Button>
        </div>
      </div>
    )
  }

  return (
    <AppointmentChat
      appointmentId={appointmentId!}
      patientId={patientId}
      providerId={providerId}
      senderRole="patient"
      providerName={providerName}
      backPath={backPath}
    />
  )
}
